// Oct 22 2026, user's explicit ask: the Confirmed Production report was
// adding up EVERY operation confirmation and calling it finished production
// (lot 76687 alone contributed RP_10 5 + RP_20 5 + RP_30 5 + END 5 + RP_10 2).
// Finished production is ONLY what passed the final reporting point - the one
// SAP marks END - and quantities are never added across units of measure.
import { formatUnit } from "./formatUnit";

export const FINAL_REPORTING_POINT_ID = "END";

export const isFinalConfirmation = (r) =>
  (r.reporting_point_id || "").trim().toUpperCase() === FINAL_REPORTING_POINT_ID;

// "OP_40 - GALVANIZE" is SAP's operation id + its name in one string; older
// records carry only the operation name ("BLANK+PUNCH+BENDING") or nothing,
// where the reporting point id is the best identifier we have.
export const splitOperation = (r) => {
  const desc = (r.reporting_point_description || "").trim();
  const match = desc.match(/^(OP[_\-\s]?\d+)\s*[-–—]\s*(.+)$/i);
  if (match) return { operationId: match[1].toUpperCase().replace(/[\s-]/g, "_"), operationName: match[2].trim() };
  return { operationId: r.reporting_point_id || "—", operationName: desc || "—" };
};

export const mainUom = (r) => formatUnit(r.uom) || "EA";
export const byproductUom = (r) => formatUnit(r.byproduct_unit_code) || "unit not set";

const bump = (map, unit, qty) => map.set(unit, (map.get(unit) || 0) + (qty || 0));

export const formatQty = (v) => (v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: 3 }));

export const formatUnitTotals = (entries) =>
  (entries.length === 0 ? "—" : entries.map(([unit, qty]) => `${formatQty(qty)} ${unit}`).join("  ·  "));

export const computeKpis = (rows) => {
  const finished = new Map();
  const operations = new Map();
  const scrap = new Map();
  const byproduct = new Map();
  let finishedTransactions = 0;
  for (const r of rows) {
    const unit = mainUom(r);
    bump(operations, unit, r.confirmed_quantity);
    bump(scrap, unit, r.confirmed_scrap);
    if (isFinalConfirmation(r)) {
      bump(finished, unit, r.confirmed_quantity);
      finishedTransactions += 1;
    }
    if (r.byproduct_confirmed_quantity) bump(byproduct, byproductUom(r), r.byproduct_confirmed_quantity);
  }
  return {
    finished: [...finished.entries()],
    operations: [...operations.entries()],
    scrap: [...scrap.entries()],
    byproduct: [...byproduct.entries()],
    transactions: rows.length,
    finishedTransactions,
  };
};

const accumulate = (group, r) => {
  group.quantity += r.confirmed_quantity || 0;
  group.scrap += r.confirmed_scrap || 0;
  // Keyed by unit: a group can hold 0.12 KG of scrap metal and 0.048 of a
  // by-product whose unit was never recorded - adding those is nonsense.
  if (r.byproduct_confirmed_quantity) bump(group.byproductByUom, byproductUom(r), r.byproduct_confirmed_quantity);
  group.confirmations += 1;
  group.transactions.push(r);
  if (!group.latestAt || r.at > group.latestAt) {
    group.latestAt = r.at;
    group.wipClearing = r.wip_clearing;
    group.actor = r.actor;
  }
};

const finish = (groups) => {
  const out = [...groups.values()];
  for (const g of out) {
    g.byproduct = [...g.byproductByUom.entries()];
    g.transactions.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }
  return out.sort((a, b) => (b.latestAt || "").localeCompare(a.latestAt || ""));
};

// Several confirmations of the SAME operation on the SAME lot collapse into
// one row carrying their total and how many there were (user's example: lot
// 76687 OP_10 confirmed 5 then 2 = 7 EA across 2 confirmations).
export const groupByOperation = (rows) => {
  const groups = new Map();
  for (const r of rows) {
    const { operationId, operationName } = splitOperation(r);
    const key = [r.site_id, r.main_output_product, r.production_model_id, r.production_lot_id, r.reporting_point_id, operationId].join("|");
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        site_id: r.site_id,
        main_output_product: r.main_output_product,
        main_output_product_description: r.main_output_product_description,
        production_model_id: r.production_model_id,
        production_lot_id: r.production_lot_id,
        reporting_point_id: r.reporting_point_id,
        operationId,
        operationName,
        isFinal: isFinalConfirmation(r),
        uom: mainUom(r),
        // Oct 31 2026: what SAP still had open at this reporting point (same
        // for every confirmation of the same lot + step, so read once).
        openQuantity: r.open_quantity ?? null,
        quantity: 0, scrap: 0, byproductByUom: new Map(),
        confirmations: 0, latestAt: null, wipClearing: null, actor: null,
        transactions: [],
      });
    }
    accumulate(groups.get(key), r);
  }
  return finish(groups);
};

// One row per production lot, built from the final reporting point only.
export const groupFinishedByLot = (rows) => {
  const groups = new Map();
  for (const r of rows) {
    if (!isFinalConfirmation(r)) continue;
    const { operationId, operationName } = splitOperation(r);
    const key = [r.site_id, r.production_lot_id, r.main_output_product].join("|");
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        site_id: r.site_id,
        main_output_product: r.main_output_product,
        main_output_product_description: r.main_output_product_description,
        production_model_id: r.production_model_id,
        production_lot_id: r.production_lot_id,
        finalOperationId: operationId,
        finalOperationName: operationName,
        uom: mainUom(r),
        openQuantity: r.open_quantity ?? null,
        quantity: 0, scrap: 0, byproductByUom: new Map(),
        confirmations: 0, latestAt: null, wipClearing: null, actor: null,
        transactions: [],
      });
    }
    accumulate(groups.get(key), r);
  }
  return finish(groups);
};
